import { pb } from '../../../shared/api/pocketbase';
import type { FetchOptionsResult } from '../../../shared/ui/Select';
import { User, UserRole } from './types';

export async function fetchManagerOptions({ page, search }: { page: number; search: string }): Promise<FetchOptionsResult> {
  const perPage = 20;
  const safe = search.trim().replace(/"/g, '');
  const filter = safe
    ? `isBlocked=false && role="MANAGER" && name ~ "${safe}"`
    : `isBlocked=false && role="MANAGER"`;
  const result = await pb.collection('users').getList<Record<string, unknown>>(page, perPage, {
    sort: 'name',
    filter,
    fields: 'id,name,email',
    requestKey: null,
  });
  return {
    items: result.items.map(u => ({ value: String(u.id), label: String(u.name ?? u.email ?? u.id) })),
    hasMore: page * perPage < result.totalItems,
  };
}

export async function fetchUsers(): Promise<User[]> {
  const records = await pb.collection('users').getFullList<Record<string, unknown>>({
    sort: 'name',
    filter: 'isBlocked=false',
    fields: 'id,email,name,role,isBlocked',
  });
  return records.map(mapUser);
}

export async function fetchUsersPage(params: {
  page: number;
  search: string;
  perPage?: number;
  allowedIds?: string[];
}): Promise<{ items: User[]; hasMore: boolean }> {
  const { page, search, perPage = 5, allowedIds } = params;
  const filterParts: string[] = ['isBlocked=false'];
  if (allowedIds?.length) {
    filterParts.push(`(${allowedIds.map(id => `id = "${id}"`).join(' || ')})`);
  }
  if (search.trim()) {
    filterParts.push(`name ~ "${search.trim().replace(/"/g, '')}"`);
  }
  const result = await pb.collection('users').getList<Record<string, unknown>>(page, perPage, {
    sort: 'name',
    filter: filterParts.join(' && '),
    fields: 'id,email,name,role,isBlocked',
  });
  return {
    items: result.items.map(mapUser),
    hasMore: page * perPage < result.totalItems,
  };
}

export async function fetchUsersByIds(ids: string[]): Promise<User[]> {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));

  if (!uniqueIds.length) {
    return [];
  }

  const records = await pb.collection('users').getFullList<Record<string, unknown>>({
    sort: 'name',
    filter: `isBlocked=false && (${uniqueIds.map(id => `id = "${id}"`).join(' || ')})`,
    fields: 'id,email,name,role,isBlocked',
  });

  return records.map(mapUser);
}

function mapUser(model: Record<string, unknown>): User {
  return {
    id: String(model.id ?? ''),
    email: String(model.email ?? ''),
    name: String(model.name ?? model.email ?? ''),
    role: normalizeRole(model.role),
    isBlocked: Boolean(model.isBlocked),
  };
}

function normalizeRole(role: unknown): UserRole {
  const normalized = String(role ?? '').toUpperCase();

  if (normalized === 'ADMIN' || normalized === 'MANAGER') {
    return normalized;
  }

  return 'USER';
}
